import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send, Sparkles, Loader2, UserRound } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../lib/api";
import { BUSINESS } from "../data/packages";

const WELCOME =
  "Hi! I'm your Cosmos Routes trip assistant. Ask me anything — destinations, prices, itineraries — or try: 'Plan a 3-day Goa trip under ₹12,000'.";

const CHIPS = [
  "Plan a 3-day Goa trip",
  "Best trip under ₹15,000?",
  "What's included in Kerala 5N/6D?",
];

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: "assistant", text: WELCOME }]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [contact, setContact] = useState({ name: "", phone: "" });
  const [sending, setSending] = useState(false);
  const sidRef = useRef(null);
  const scrollRef = useRef(null);

  if (!sidRef.current) {
    let sid = localStorage.getItem("cosmos_chat_sid");
    if (!sid) {
      sid = crypto.randomUUID ? crypto.randomUUID() : String(Math.random()).slice(2);
      localStorage.setItem("cosmos_chat_sid", sid);
    }
    sidRef.current = sid;
  }

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages, showForm]);

  const send = async (raw) => {
    const text = (raw ?? input).trim();
    if (!text || streaming) return;
    setMessages((m) => [...m, { role: "user", text }, { role: "assistant", text: "" }]);
    setInput("");
    setStreaming(true);
    const ctrl = new AbortController();
    const kill = setTimeout(() => ctrl.abort(), 45000);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sidRef.current, message: text }),
        signal: ctrl.signal,
      });
      if (!res.ok || !res.body) throw new Error("bad response");
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          const line = p.trim();
          if (!line.startsWith("data:")) continue;
          const payload = JSON.parse(line.slice(5).trim());
          if (payload.t) {
            setMessages((m) => {
              const c = [...m];
              const last = c[c.length - 1];
              c[c.length - 1] = { ...last, text: last.text + payload.t };
              return c;
            });
          }
          if (payload.done && payload.notified) {
            toast.success("Got your number! Our team will WhatsApp you shortly.");
          }
        }
      }
    } catch (e) {
      setMessages((m) => {
        const c = [...m];
        const last = c[c.length - 1];
        c[c.length - 1] = { ...last, text: last.text || "Sorry, I couldn't answer that right now. Please try again or WhatsApp us directly." };
        return c;
      });
    } finally {
      clearTimeout(kill);
      setStreaming(false);
    }
  };

  const shareContact = async (e) => {
    e.preventDefault();
    if (sending) return;
    if (contact.name.trim().length < 2 || contact.phone.trim().length < 10) {
      toast.error("Please enter your name and a 10-digit WhatsApp number.");
      return;
    }
    setSending(true);
    try {
      const interest =
        [...messages].reverse().find((m) => m.role === "user")?.text || "";
      await axios.post(`${API}/chat-enquiry`, {
        name: contact.name.trim(),
        phone: contact.phone.trim(),
        interest: interest.slice(0, 300),
        session_id: sidRef.current,
      });
      setMessages((m) => [
        ...m,
        { role: "assistant", text: `Thanks ${contact.name.trim()}! Our team will contact you on WhatsApp (${contact.phone.trim()}) very soon. Meanwhile, ask me anything else!` },
      ]);
      setShowForm(false);
      setContact({ name: "", phone: "" });
      toast.success("Details sent! Our team will reach out soon.");
    } catch (err) {
      toast.error("Could not send your details. Please try again or WhatsApp us.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button
        data-testid="chat-launcher-btn"
        aria-label="Open AI trip assistant"
        onClick={() => setOpen((o) => !o)}
        className={`fixed bottom-6 left-6 z-[85] flex items-center gap-2.5 rounded-full py-3.5 pl-4 pr-5 font-semibold shadow-[0_10px_35px_rgba(123,44,191,0.45)] transition-all duration-300 hover:-translate-y-1 ${
          open ? "bg-obsidian text-slate-100 border border-white/15" : "bg-nebula text-white"
        }`}
      >
        <Sparkles className="h-5 w-5 text-aurora" />
        {!open && <span className="hidden text-sm sm:block">Trip Assistant</span>}
        {open && <X className="h-4 w-4" />}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.97 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            data-testid="chat-window"
            className="fixed bottom-24 left-4 right-4 z-[86] flex h-[26rem] max-h-[70vh] w-auto flex-col overflow-hidden rounded-3xl border border-white/10 bg-obsidian/95 shadow-2xl backdrop-blur-xl sm:left-6 sm:right-auto sm:w-[23rem]"
          >
            <div className="flex items-center justify-between border-b border-white/10 px-5 py-3.5">
              <div>
                <p className="font-display text-sm font-bold text-slate-50">Trip Assistant</p>
                <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-aurora">
                  AI · Online now
                </p>
              </div>
              <button
                data-testid="chat-share-btn"
                onClick={() => setShowForm((s) => !s)}
                className="flex items-center gap-1.5 rounded-full border border-aurora/40 px-3 py-1.5 text-[10px] font-bold text-aurora transition-all hover:bg-aurora hover:text-ink"
              >
                <UserRound className="h-3 w-3" /> Get a callback
              </button>
            </div>

            {showForm && (
              <form onSubmit={shareContact} data-testid="chat-contact-form" className="border-b border-white/10 bg-aurora/5 px-5 py-4">
                <p className="text-[11px] leading-snug text-slate-300">
                  Share your details — we'll WhatsApp you a ready plan.
                </p>
                <div className="mt-2.5 flex gap-2">
                  <input
                    data-testid="chat-share-name-input"
                    value={contact.name}
                    onChange={(e) => setContact((c) => ({ ...c, name: e.target.value }))}
                    placeholder="Your name"
                    className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-xs text-slate-100 placeholder:text-slate-500 outline-none focus:border-aurora/60"
                  />
                  <input
                    data-testid="chat-share-phone-input"
                    value={contact.phone}
                    onChange={(e) => setContact((c) => ({ ...c, phone: e.target.value }))}
                    placeholder="WhatsApp number"
                    inputMode="tel"
                    className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-xs text-slate-100 placeholder:text-slate-500 outline-none focus:border-aurora/60"
                  />
                </div>
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="chat-share-submit-btn"
                  className="mt-2.5 flex w-full items-center justify-center gap-2 rounded-lg bg-aurora py-2 text-xs font-bold text-ink disabled:opacity-60"
                >
                  {sending && <Loader2 className="h-3 w-3 animate-spin" />}
                  {sending ? "Sending…" : "Send to our team"}
                </button>
              </form>
            )}

            <div ref={scrollRef} data-testid="chat-messages" className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    data-testid={`chat-msg-${m.role}`}
                    className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-3.5 py-2.5 text-[13px] leading-relaxed ${
                      m.role === "user"
                        ? "rounded-br-sm bg-aurora text-ink"
                        : "rounded-bl-sm border border-white/10 bg-white/5 text-slate-200"
                    }`}
                  >
                    {m.text || (streaming && i === messages.length - 1 ? "…" : "")}
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-white/10 px-4 py-3">
              {messages.length <= 2 && (
                <div className="mb-2.5 flex flex-wrap gap-1.5">
                  {CHIPS.map((c) => (
                    <button
                      key={c}
                      data-testid="chat-suggestion-chip"
                      onClick={() => send(c)}
                      className="rounded-full border border-white/15 px-3 py-1.5 text-[10px] text-slate-300 transition-all hover:border-aurora/50 hover:text-aurora"
                    >
                      {c}
                    </button>
                  ))}
                </div>
              )}
              <div className="flex items-center gap-2">
                <input
                  data-testid="chat-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && send()}
                  placeholder="Ask about any trip…"
                  className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-[13px] text-slate-100 placeholder:text-slate-500 outline-none focus:border-aurora/60"
                />
                <button
                  data-testid="chat-send-btn"
                  onClick={() => send()}
                  disabled={streaming || !input.trim()}
                  className="shrink-0 rounded-xl bg-aurora p-2.5 text-ink transition-all hover:shadow-[0_0_20px_rgba(0,245,212,0.4)] disabled:opacity-40"
                >
                  {streaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </button>
              </div>
              <p className="mt-2 text-center font-mono text-[8px] uppercase tracking-[0.2em] text-slate-600">
                AI answers · For booking, WhatsApp {BUSINESS.phonePrimary}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
